import React, { useState, useEffect, useRef, useId } from 'react';
import { NodeViewWrapper } from '@tiptap/react';
import {
  Box,
  IconButton,
  Tooltip,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Collapse,
  Skeleton,
} from '@mui/material';
import {
  Edit,
  Visibility,
  ContentCopy,
  Delete,
  Check,
  Close,
  AccountTree,
} from '@mui/icons-material';
import { loadMermaid, sanitizeMermaidSvg } from '../utils/mermaidExportHelper';
import useEditorEditable from './editor/useEditorEditable';

/**
 * Observe when an element first scrolls near the viewport.
 * @param {React.RefObject<HTMLElement>} ref - Element to observe.
 * @returns {boolean} True once the element has been visible.
 */
const useHasBeenVisible = (ref) => {
  const [visible, setVisible] = useState(typeof IntersectionObserver === 'undefined');
  useEffect(() => {
    if (visible || !ref.current) return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        setVisible(true);
        observer.disconnect();
      }
    }, { rootMargin: '400px 0px' });
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref, visible]);
  return visible;
};

/**
 * Interactive React node-view for a Mermaid diagram inside TipTap.
 *
 * Mermaid is loaded on demand (dynamic import) and a diagram only renders once it scrolls
 * near the viewport, so documents with many diagrams open quickly. Shows inline review state
 * (`data-diff-block`) and hides editing controls when the editor is read-only.
 *
 * @param {Object} props - TipTap node view props.
 * @param {Object} props.node - ProseMirror node.
 * @param {Object} props.editor - TipTap editor.
 * @param {Function} props.updateAttributes - Update node attributes.
 * @param {Function} props.deleteNode - Delete this node.
 * @param {boolean} props.selected - Whether the node is currently selected.
 * @returns {JSX.Element}
 */
const MermaidNodeView = ({ node, editor, updateAttributes, deleteNode, selected }) => {
  const [svgOutput, setSvgOutput] = useState('');
  const [renderError, setRenderError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [draftCode, setDraftCode] = useState(node.attrs.code || '');
  const containerRef = useRef(null);
  const visible = useHasBeenVisible(containerRef);
  const uniqueId = useId().replace(/:/g, '-');
  const diagramId = `mermaid-${uniqueId}`;
  const editable = useEditorEditable(editor);
  const diffState = node.attrs.diffBlock;

  useEffect(() => {
    if (!visible) return undefined;
    let cancelled = false;
    const code = node.attrs.code;
    if (!code?.trim()) {
      setSvgOutput('');
      setRenderError('Empty diagram');
      return undefined;
    }
    loadMermaid()
      .then((mermaid) => mermaid.render(diagramId, code.trim()))
      .then(({ svg }) => {
        if (cancelled) return;
        setSvgOutput(sanitizeMermaidSvg(svg));
        setRenderError('');
      })
      .catch((err) => {
        if (cancelled) return;
        setRenderError(err?.message || 'Invalid Mermaid syntax');
        setSvgOutput('');
      })
      .finally(() => {
        // mermaid leaves error containers in the DOM on failure
        const errEl = document.getElementById(`d${diagramId}`);
        if (errEl) errEl.remove();
      });
    return () => {
      cancelled = true;
    };
  }, [node.attrs.code, diagramId, visible]);

  /** Apply the draft code to the node and close the editor. */
  const handleSave = () => {
    updateAttributes({ code: draftCode });
    setIsEditing(false);
  };

  /** Discard edits and close the editor. */
  const handleCancel = () => {
    setDraftCode(node.attrs.code || '');
    setIsEditing(false);
  };

  /** Copy the mermaid source to the clipboard. */
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(node.attrs.code || '');
    } catch {
      /* clipboard may not be available in insecure contexts */
    }
  };

  const borderColor = diffState === 'delete'
    ? 'error.main'
    : diffState === 'insert'
      ? 'success.main'
      : selected ? 'primary.main' : 'divider';

  return (
    <NodeViewWrapper as="div" data-mermaid-block="true" data-diff-block={diffState || undefined}>
      <Paper
        elevation={0}
        sx={{
          my: 2,
          border: '1px solid',
          borderColor,
          borderRadius: 'var(--q-radius-md, 10px)',
          overflow: 'hidden',
          opacity: diffState === 'delete' ? 0.6 : 1,
          transition: 'border-color 0.2s, box-shadow 0.2s',
          boxShadow: selected ? '0 0 0 3px rgba(79,59,214,0.12)' : 'none',
          '&:hover': editable ? { borderColor: diffState ? borderColor : 'primary.light' } : undefined,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 1.5,
            py: 0.5,
            backgroundColor: 'action.hover',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
          contentEditable={false}
        >
          <Stack direction="row" spacing={0.5} alignItems="center">
            <AccountTree fontSize="small" color="primary" />
            <Typography variant="caption" fontWeight={600} color="text.secondary">
              {diffState === 'delete' ? 'Diagram (will be removed)' : diffState === 'insert' ? 'Diagram (new)' : 'Mermaid diagram'}
            </Typography>
          </Stack>

          {editable && (
            <Stack direction="row" spacing={0.25}>
              <Tooltip title={isEditing ? 'Preview' : 'Edit source'}>
                <IconButton
                  size="small"
                  aria-label={isEditing ? 'Preview diagram' : 'Edit diagram source'}
                  onClick={() => {
                    if (!isEditing) setDraftCode(node.attrs.code || '');
                    setIsEditing((prev) => !prev);
                  }}
                >
                  {isEditing ? <Visibility fontSize="small" /> : <Edit fontSize="small" />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Copy source">
                <IconButton size="small" aria-label="Copy diagram source" onClick={handleCopy}>
                  <ContentCopy fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Remove diagram">
                <IconButton size="small" aria-label="Remove diagram" onClick={deleteNode} color="error">
                  <Delete fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          )}
        </Box>

        {!isEditing && (
          <Box
            ref={containerRef}
            contentEditable={false}
            sx={{
              p: 2,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 120,
              overflow: 'auto',
              '& svg': { maxWidth: '100%', height: 'auto' },
            }}
          >
            {svgOutput ? (
              <div dangerouslySetInnerHTML={{ __html: svgOutput }} />
            ) : renderError ? (
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="body2" color="error" gutterBottom>
                  Diagram render error
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}
                >
                  {renderError}
                </Typography>
              </Box>
            ) : (
              <Skeleton variant="rounded" width="70%" height={96} aria-label="Loading diagram" />
            )}
          </Box>
        )}

        <Collapse in={isEditing && editable} unmountOnExit>
          <Box sx={{ p: 2, pt: 1 }}>
            <TextField
              multiline
              fullWidth
              minRows={6}
              maxRows={20}
              value={draftCode}
              onChange={(e) => setDraftCode(e.target.value)}
              placeholder="graph TD&#10;  A[Start] --> B[End]"
              variant="outlined"
              inputProps={{ 'aria-label': 'Mermaid source' }}
              InputProps={{
                sx: {
                  fontFamily: '"Courier New", monospace',
                  fontSize: '0.85rem',
                  lineHeight: 1.6,
                },
              }}
              sx={{ mb: 1 }}
            />
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button size="small" variant="outlined" startIcon={<Close />} onClick={handleCancel}>
                Cancel
              </Button>
              <Button size="small" variant="contained" startIcon={<Check />} onClick={handleSave}>
                Apply
              </Button>
            </Stack>
          </Box>
        </Collapse>
      </Paper>
    </NodeViewWrapper>
  );
};

export default MermaidNodeView;
